import { INITIAL_PAGE, PAGINATION_LIMIT } from '@repo/toolkit/constants/pagination'
import { Option } from '@repo/toolkit/types'
import { PaginationRequest, PaginationResponse } from '@repo/ui/data-table'
import SelectInput from '@repo/ui/select-input'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { ApiUrls } from '../../../api/apis.config'
import { restApiClient } from '../../../api/rest-api-client'
import { encodeNestedObject } from '../../../lib/utils/functions/misc.functions'
import { useFormField } from '../forms/form'

type RequiredFields = {
  id: number
}

type Props<T extends RequiredFields> = {
  endpoint: ApiUrls
  selectedOption?: T
  label?: string
  required?: boolean
  dropDownPosition?: 'under' | 'above'
  mapper: (record: T) => Option<number>
  onChange?: (value: Option<number>) => void
}

const ControlledDynamicSelectInput = <T extends RequiredFields>({
  endpoint,
  selectedOption,
  mapper,
  dropDownPosition,
  ...props
}: Props<T>) => {
  const { error, name } = useFormField()
  const { trigger } = useForm()
  const [search, setSearch] = useState('')
  const [text, setText] = useState('')

  const { data } = useQuery({
    queryKey: [endpoint, search],
    queryFn: () =>
      restApiClient
        .url(
          encodeNestedObject<PaginationRequest>(endpoint, {
            page: INITIAL_PAGE,
            limit: PAGINATION_LIMIT,
            search,
            version: 'LIGHT'
          })
        )
        .get<PaginationResponse<T>>()
  })

  useEffect(() => {
    const timeout = setTimeout(() => setSearch(text), 500)

    return () => clearTimeout(timeout)
  }, [text])

  return (
    <SelectInput<number>
      {...props}
      searchable
      dropDownPosition={dropDownPosition}
      selectedOption={
        selectedOption
          ? {
              value: mapper(selectedOption).value,
              label: mapper(selectedOption).label
            }
          : undefined
      }
      options={data?.data.map((record) => ({
        value: mapper(record).value,
        label: `${mapper(record).label}`
      }))}
      onTextChange={setText}
      errorMessage={error ? String(error?.message) : undefined}
      onBlur={() => trigger(name)}
      onChange={(value) => {
        const record = data?.data.find((record) => mapper(record).value === value)
        if (record) props.onChange?.(mapper(record))
      }}
    />
  )
}

export default ControlledDynamicSelectInput
